<template>
    <page-wrapper>
        <page-header>
            <template v-slot:breadcrumbs>
                <q-breadcrumbs-el :label="$tc('common.accounting.posting.posting', 2)" :to="{ name: 'accounting-posting-list' }" />
            </template>

            <h1>{{ $tc('common.accounting.posting.posting', 2) }}
                <small v-if="postingFilter.data.advancedFilters.dateFrom && postingFilter.data.advancedFilters.dateUntil" class="subtitle">{{ dateRangeTextAddition }}</small>
            </h1>
        </page-header>

        <!--region Advanced filters-->
        <q-list bordered class="rounded-borders do-not-print">
            <q-expansion-item
                group="date-filter"
                :class="['advanced-filter-expansion-item rounded-borders', { 'is-active': postingFilter.data.advancedFilters.dateFrom || postingFilter.data.advancedFilters.dateUntil }]"
                expand-icon="mib-arrow-left-1"
                expanded-icon="mib-arrow-down-1"
                default-opened
            >
                <template v-slot:header>
                    <q-item-section>
                        {{ $t('views.accounting.posting.filter-by-posting-date') }}
                    </q-item-section>
                    <q-item-section side>
                        <div class="row items-center">
                            <q-btn
                                v-if="postingFilter.data.advancedFilters.dateFrom || postingFilter.data.advancedFilters.dateUntil"
                                color="primary"
                                size="sm"
                                flat
                                round
                                icon="mib-filter-2"
                                @click="postingFilter.clearDateFilter()"
                            >
                                <q-tooltip
                                    anchor="center start"
                                    self="center right"
                                    :delay="1000"
                                    :offset="[10, 0]"
                                >
                                    {{ $tc('common.filter.filter-is-active--click-to-reset', 1) }}
                                </q-tooltip>
                            </q-btn>
                        </div>
                    </q-item-section>
                </template>
                <q-card class="rounded-borders">
                    <q-card-section class="q-pt-xs q-pl-xs q-col-gutter-x-sm rounded-borders">
                        <q-list class="row">
                            <!--region Default date range-->
                            <q-item class="q-pr-none q-pl-none col-xs-12 col-lg-4">
                                <q-item-section avatar>
                                    <q-radio
                                        v-model="postingFilter.data.advancedFilters.dateRangeFilterType"
                                        :val="DateRangeFilterType.DEFAULT"
                                    />
                                </q-item-section>
                                <q-item-section>
                                    <q-item-label class="q-mb-xs">{{ $t('views.task.list.filter-by-due-date--date-range') }}</q-item-label>
                                    <div class="row q-col-gutter-sm">
                                        <div class="col-xs-6 col-sm-4 col-md-3 col-lg-6">
                                            <calendar-input
                                                v-model="postingFilter.data.advancedFilters.dateFrom"
                                                :placeholder="$t('common.term.date-from')"
                                                :disable="postingFilter.data.advancedFilters.dateRangeFilterType !== DateRangeFilterType.DEFAULT"
                                            />
                                        </div>
                                        <div class="col-xs-6 col-sm-4 col-md-3 col-lg-6">
                                            <calendar-input
                                                v-model="postingFilter.data.advancedFilters.dateUntil"
                                                :placeholder="$t('common.term.date-until')"
                                                :disable="postingFilter.data.advancedFilters.dateRangeFilterType !== DateRangeFilterType.DEFAULT"
                                            />
                                        </div>
                                    </div>
                                </q-item-section>
                            </q-item>
                            <!--endregion-->

                            <!--region Dynamic date range-->
                            <q-item class="q-pr-none q-pl-none col-xs-12 col-lg-6 offset-lg-1">
                                <q-item-section avatar>
                                    <q-radio
                                        v-model="postingFilter.data.advancedFilters.dateRangeFilterType"
                                        :val="DateRangeFilterType.DYNAMIC"
                                    />
                                </q-item-section>
                                <q-item-section>
                                    <q-item-label class="q-mb-xs">{{ $t('views.task.list.filter-by-due-date--dynamic-date-range') }}</q-item-label>
                                    <div class="dynamic-date-range-wrapper row q-col-gutter-sm">
                                        <div class="input-wrapper col-xs-4 col-sm-3 col-md-3 col-lg-3 col-xl-2">
                                            <q-btn
                                                class="due-date-range-nav-button q-mr-sm"
                                                flat
                                                color="secondary"
                                                size="sm"
                                                icon="mib-arrow-left-1"
                                                :disabled="postingFilter.data.advancedFilters.dateRangeFilterType !== DateRangeFilterType.DYNAMIC"
                                                @click="() => postingFilter.shiftDateRangeEarlier()"
                                            >
                                                <q-tooltip v-if="postingFilter.data.advancedFilters.dateRangeUnit" :delay="1000" :offset="[0, 10]">{{ $tc(`common.time.${postingFilter.data.advancedFilters.dateRangeUnit.toLowerCase()}--previous-n-${postingFilter.data.advancedFilters.dateRangeUnit.toLowerCase()}s`, postingFilter.data.advancedFilters.dateRangeAmount) }}</q-tooltip>
                                            </q-btn>

                                            <base-input
                                                v-model.number="postingFilter.data.advancedFilters.dateRangeAmount"
                                                type="number"
                                                min="1"
                                                step="1"
                                                :disable="postingFilter.data.advancedFilters.dateRangeFilterType !== DateRangeFilterType.DYNAMIC"
                                            />
                                        </div>
                                        <div class="input-wrapper col-xs-8 col-sm-5 col-md-3 col-lg-4 col-xl-4">
                                            <!-- TODO @TFU: Get label translations in :options with "dynamic" amount -->
                                            <q-select
                                                v-model="postingFilter.data.advancedFilters.dateRangeUnit"
                                                :options="dateRangeUnitOptions"
                                                emit-value
                                                map-options
                                                dense
                                                filled
                                                square
                                                :disable="postingFilter.data.advancedFilters.dateRangeFilterType !== DateRangeFilterType.DYNAMIC"
                                            />

                                            <q-btn
                                                class="due-date-range-nav-button q-ml-sm"
                                                flat
                                                color="secondary"
                                                size="sm"
                                                icon="mib-arrow-right-1"
                                                :disabled="postingFilter.data.advancedFilters.dateRangeFilterType !== DateRangeFilterType.DYNAMIC"
                                                @click="() => postingFilter.shiftDateRangeLater()"
                                            >
                                                <q-tooltip v-if="postingFilter.data.advancedFilters.dateRangeUnit" :delay="1000" :offset="[0, 10]">{{ $tc(`common.time.${postingFilter.data.advancedFilters.dateRangeUnit.toLowerCase()}--next-n-${postingFilter.data.advancedFilters.dateRangeUnit.toLowerCase()}s`, postingFilter.data.advancedFilters.dateRangeAmount) }}</q-tooltip>
                                            </q-btn>
                                        </div>
                                    </div>
                                </q-item-section>
                            </q-item>
                        <!--endregion-->
                        </q-list>
                    </q-card-section>
                </q-card>
            </q-expansion-item>
        </q-list>
        <!--endregion-->

        <!--region Postings table-->
        <base-table
            ref="PostingListTable"
            :columns="columns"
            :fetch-objects-fn="Posting.service.all"
            fetch-objects-abort-previous-request
            :additional-filters="additionalFilters"
            user-settings-base-path="PostingList"
            pagination-sort-by-default-key="number"
            pagination-initial-order-direction-descending
            :pagination-max-page="10"
            @row-dblclick="onDblClick"
        >
            <!-- TODO improvement: This could potentially be refactored as it uses the same code as the BaseTable in `src/components/contract/ContractCommissionPostingsList.vue` -->
            <template v-slot:body-cell-number="slotProps">
                <q-td
                    :props="slotProps"
                    class="text-right"
                    data-test="td:posting-number"
                    auto-width
                >
                    {{ slotProps.row.number.number }}
                </q-td>
            </template>

            <template v-slot:body-cell-debit_account_number="slotProps">
                <q-td :props="slotProps" class="truncate-text" data-test="td:debit-account-number">
                    <span>
                        <span class="account-number">{{ slotProps.row.debitAccount.number }}</span> <span class="account-name additional-info">– {{ slotProps.row.debitAccount.name }}</span>
                        <q-tooltip
                            :delay="1000"
                            anchor="center start"
                            self="center start"
                            :offset="[0, 0]"
                            max-width="70ch"
                        >{{ slotProps.row.debitAccount.number }} – {{ slotProps.row.debitAccount.name }}</q-tooltip>
                    </span>
                </q-td>
            </template>

            <template v-slot:body-cell-credit_account_number="slotProps">
                <q-td :props="slotProps" class="truncate-text" data-test="td:credit-account-number">
                    <span>
                        <span class="account-number">{{ slotProps.row.creditAccount.number }}</span> <span class="account-name additional-info">– {{ slotProps.row.creditAccount.name }}</span>
                        <q-tooltip
                            :delay="1000"
                            anchor="center start"
                            self="center start"
                            :offset="[0, 0]"
                            max-width="70ch"
                        >{{ slotProps.row.creditAccount.number }} – {{ slotProps.row.creditAccount.name }}</q-tooltip>
                    </span>
                </q-td>
            </template>

            <template v-slot:body-cell-amount="slotProps">
                <q-td :props="slotProps" data-test="td:amount">
                    <span :class="['amount', { 'text-negative-darker': slotProps.row.amount < 0 }]">{{ slotProps.row.formattedAmount }}</span>
                </q-td>
            </template>

            <template v-slot:body-cell-text="slotProps">
                <q-td :props="slotProps" class="truncate-text" data-test="td:posting-text">
                    <info-icon
                        v-if="slotProps.row.is_confidential"
                        name="mib-lock-1"
                        :text="$tc('common.accounting.posting.is-confidential--description', 1)"
                        class="q-mr-xs"
                        color="secondary"
                    />
                    <span>
                        {{ slotProps.row.text }}
                        <q-tooltip
                            :delay="1000"
                            anchor="center start"
                            self="center start"
                            :offset="[0, 0]"
                            max-width="70ch"
                        >{{ slotProps.row.text }}</q-tooltip>
                    </span>
                </q-td>
            </template>

            <template v-slot:body-cell-actions="slotProps">
                <q-td :props="slotProps" data-test="td:actions" auto-width>
                    <q-btn
                        color="primary"
                        size="sm"
                        flat
                        round
                        icon="mib-pencil"
                        :to="{ name: 'accounting-posting-update', params: { id: slotProps.row.id } }"
                    >
                        <q-tooltip :delay="1000" :offset="[0, 10]">{{ $tc('common.accounting.posting.show-posting', 1) }}</q-tooltip>
                    </q-btn>
                </q-td>
            </template>
        </base-table>
        <!--endregion-->

        <in-page-footer>
            <base-button
                :label="$tc('views.accounting.posting.add-posting', 1)"
                primary-button
                :to="{ name: 'accounting-posting-create' }"
            />
        </in-page-footer>
    </page-wrapper>
</template>

<script>
import BaseInput from '@/components/form/BaseInput.vue'
import CalendarInput from '@/components/form/CalendarInput.vue'
import { useI18n } from '@/composables/i18n'
import { useRouter } from '@/composables/router'
import { DateRangeFilterType, DateRangeUnitOptions } from '@/enums'
import { PostingFilter } from '@/filters/PostingFilter'
import { onMounted, onBeforeUnmount, ref, computed } from 'vue'
import { EventBus } from '@/event-bus'
import { Posting } from '@/models/posting'
import BaseTable from '@/components/BaseTable'
import { lightFormat } from 'date-fns'
import { formatDate } from '@/helpers/date'

export default {
    name: 'PostingList',
    methods: { formatDate, lightFormat },
    meta () {
        return {
            title: this.dateRangeTextAddition ? `${this.$tc('common.accounting.posting.posting', 2)} ${this.dateRangeTextAddition}` : this.$tc('common.accounting.posting.posting', 2),
        }
    },
    components: {
        BaseInput,
        BaseTable,
        CalendarInput,
    },
    setup () {
        const { tc } = useI18n()
        const router = useRouter()

        // Misc
        const dateRangeTextAddition = computed(() => {
            if (postingFilter.data.advancedFilters.dateFrom && postingFilter.data.advancedFilters.dateUntil) {
                if (postingFilter.data.advancedFilters.dateFrom === postingFilter.data.advancedFilters.dateUntil) {
                    return tc('common.time.from--date', 1, { date: formatDate(postingFilter.data.advancedFilters.dateUntil) })
                }
                else {
                    return tc('common.term.from-x-to-y--short', 1, { x: formatDate(postingFilter.data.advancedFilters.dateFrom), y: formatDate(postingFilter.data.advancedFilters.dateUntil) })
                }
            }
            return ''
        })

        // Filters
        const postingFilter = new PostingFilter()
        const dateRangeUnitOptions = [
            { label: tc('common.time.day', postingFilter.data.dateRangeAmount), value: DateRangeUnitOptions.DAY },
            { label: tc('common.time.week', postingFilter.data.dateRangeAmount), value: DateRangeUnitOptions.WEEK },
            { label: tc('common.time.month', postingFilter.data.dateRangeAmount), value: DateRangeUnitOptions.MONTH },
        ]

        const additionalFilters = computed(() => {
            const filters = {}
            Object.assign(filters, postingFilter.variables.value)
            return filters
        })

        function triggerShiftDateRangeEarlier () {
            if (postingFilter.data.advancedFilters.dateRangeFilterType === DateRangeFilterType.DYNAMIC) {
                postingFilter.shiftDateRangeEarlier()
            }
        }

        function triggerShiftDateRangeLater () {
            if (postingFilter.data.advancedFilters.dateRangeFilterType === DateRangeFilterType.DYNAMIC) {
                postingFilter.shiftDateRangeLater()
            }
        }

        // Table columns
        const columns = ref([
            {
                name: 'date',
                label: tc('common.accounting.posting.date', 1),
                field: row => row.formattedDate,
                align: 'center',
                sortable: true,
            },
            {
                name: 'number',
                label: tc('common.accounting.posting.number', 1),
                align: 'left',
                sortable: true,
            },
            {
                name: 'debit_account_number',
                label: tc('common.accounting.posting.debit-account', 1),
                align: 'left',
                sortable: true,
            },
            {
                name: 'credit_account_number',
                label: tc('common.accounting.posting.credit-account', 1),
                align: 'left',
                sortable: true,
            },
            {
                name: 'amount',
                label: tc('common.accounting.posting.amount', 1),
                align: 'right',
                sortable: true,
            },
            {
                name: 'text',
                label: tc('common.accounting.posting.text', 1),
                align: 'left',
                sortable: true,
            },
            {
                name: 'type',
                label: tc('common.accounting.posting.type', 1),
                field: row => row.type.name,
                align: 'left',
                sortable: true,
            },
            {
                name: 'attachments',
                label: tc('common.accounting.posting.attachment', 2),
                field: row => row.attachments.length,
                align: 'center',
            },
            { name: 'actions', label: tc('common.term.action', 2), align: 'left', headerClasses: 'do-not-print', classes: 'do-not-print' },
        ])

        // Navigation handlers
        function onNewEntity () {
            router.push({ name: 'accounting-posting-create' })
        }

        function onDblClick (evt, row) {
            router.push({ name: 'accounting-posting-update', params: { id: row.id } })
        }

        // Keyboard shortcut events
        onMounted(() => {
            EventBus.$on('shortcut:newEntity', onNewEntity)
            EventBus.$on('shortcut:left', triggerShiftDateRangeEarlier)
            EventBus.$on('shortcut:right', triggerShiftDateRangeLater)
        })

        onBeforeUnmount(() => {
            EventBus.$off('shortcut:newEntity', onNewEntity)
            EventBus.$off('shortcut:left', triggerShiftDateRangeEarlier)
            EventBus.$off('shortcut:right', triggerShiftDateRangeLater)
        })

        return {
            // Misc
            Posting,
            dateRangeTextAddition,
            DateRangeUnitOptions,
            DateRangeFilterType,
            onNewEntity,
            onDblClick,

            // Filters
            postingFilter,
            dateRangeUnitOptions,
            additionalFilters,

            // Table
            columns,
        }
    },
}
</script>

<style lang="scss" scoped>
.truncate-text {
    max-width: 40ch;
    overflow: hidden;
    text-overflow: ellipsis;
}

.account-number,
.amount {
    font-family: $fontFamilyMono; // TODO @MTR: Check if this makes sense (and is needed).
    font-weight: bold;
    font-variant-numeric: slashed-zero;
}

// Filter
// ----------------------------------------------------------------------------------------------------
.advanced-filter-wrapper {
    border-top: 1px solid var(--color-border-primary);
    border-bottom: 1px solid var(--color-border-primary);
}

::v-deep .advanced-filter-expansion-item {
    background: var(--color-background-secondary);

    &.is-active {
        //background: var(--q-color-accent-lighter);
        background: var(--q-color-primary-lighter);

        & > .q-expansion-item__container > .q-item {
            &::after {
                content: "";
                position: absolute;
                left: 0;
                bottom: 0;
                width: 100%;
                height: 0;

                // TODO improvement: Add high-contrast version for better accessibility (also for the `toggle-color` above)
                border-bottom: 2px solid var(--q-color-primary-light);

                pointer-events: none;
            }
        }
    }
}

::v-deep .dynamic-date-range-wrapper {
    .input-wrapper {
        display: flex;
        align-self: flex-start;

        label {
            flex: 1 1 auto;
        }
    }

    .due-date-range-nav-button {
        width: 32px;
    }
}
</style>
